import { z } from 'zod'
import { HouseholdId, RecipeId, UserId } from './ids.ts'

// ISO yyyy-mm-dd. Branded so it can't be confused with an arbitrary string.
// The format is also the natural sort key for the DDB sort key (MEALPLAN#…).
export const MealDate = z
	.string()
	.regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected yyyy-mm-dd')
	.brand<'MealDate'>()
export type MealDate = z.infer<typeof MealDate>

export const MealTitle = z.string().trim().min(1).max(200)
export type MealTitle = z.infer<typeof MealTitle>

export const MealNote = z.string().trim().min(1).max(200)
export type MealNote = z.infer<typeof MealNote>

// One entity per date per household. Either field may be null — but if both
// title and note are null, the row is deleted server-side rather than stored
// as an empty husk (keeps range queries dense). `recipeId` is a deep-link
// anchor only; the meal's display string lives in `title` so a renamed or
// deleted recipe still reads correctly historically.
export const MealPlanDay = z.object({
	householdId: HouseholdId,
	date: MealDate,
	title: MealTitle.nullable(),
	note: MealNote.nullable(),
	// `.optional()` so DDB rows persisted before recipe-linking landed parse
	// cleanly. Treat `undefined` as `null` everywhere we render or send.
	recipeId: RecipeId.nullable().optional(),
	updatedAt: z.iso.datetime(),
	updatedBy: UserId,
})
export type MealPlanDay = z.infer<typeof MealPlanDay>

export const MealPlanListResponse = z.object({
	days: z.array(MealPlanDay),
})
export type MealPlanListResponse = z.infer<typeof MealPlanListResponse>

// PUT replaces the whole day. All fields nullable; if title and note are
// both null the server deletes the row and the response carries `day: null`.
// `recipeId` follows the same shape — clients send the current state, not
// a patch.
export const MealPlanPutBody = z.object({
	title: MealTitle.nullable(),
	note: MealNote.nullable(),
	recipeId: RecipeId.nullable(),
})
export type MealPlanPutBody = z.infer<typeof MealPlanPutBody>

export const MealPlanPutResponse = z.object({
	day: MealPlanDay.nullable(),
})
export type MealPlanPutResponse = z.infer<typeof MealPlanPutResponse>
